import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "../lib/toast";
import { Button } from "../components/ui/Button";
import { EmptyState } from "../components/ui/EmptyState";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { BulkActionBar } from "../components/ui/BulkActionBar";
import { Highlight } from "../components/ui/Highlight";
import { PageHeader } from "../components/ui/PageHeader";
import { Pagination } from "../components/ui/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { Toolbar, ToolbarGrow } from "../components/ui/Toolbar";
import { RowAction, RowActions } from "../components/ui/RowAction";
import { Table, type TableColumn } from "../components/ui/Table";
import { api, apiErrorMessage } from "../lib/api";
import { formatDate } from "../lib/format";
import { useClientTable } from "../lib/useClientTable";
import { useMinimumDuration } from "../lib/useMinimumDuration";

interface Template {
  _id: string;
  name: string;
  body: string;
  createdAt: string;
}

function truncate(text: string, max: number): string {
  return text.length <= max ? text : text.slice(0, max) + "…";
}

export function TemplatesPage() {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const queryClient = useQueryClient();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Template | null>(null);
  const [name, setName] = useState("");
  const [body, setBody] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<Template | null>(null);
  const [selected, setSelected] = useState<Set<React.Key>>(() => new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const templatesQuery = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await api.get<Template[]>("/templates")).data,
  });

  const templatesQueryLoading = useMinimumDuration(templatesQuery.isLoading);

  function openCreate() {
    setEditing(null);
    setName("");
    setBody("");
    setEditorOpen(true);
  }

  function openEdit(row: Template) {
    setEditing(row);
    setName(row.name);
    setBody(row.body);
    setEditorOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        return (await api.patch(`/templates/${editing._id}`, { name, body }))
          .data;
      }
      return (await api.post("/templates", { name, body })).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Template updated." : "Template created.");
      setEditorOpen(false);
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not save the template.")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => api.delete(`/templates/${id}`),
    onSuccess: () => {
      toast.success("Template deleted.");
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ["templates"] });
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "Could not delete the template.")),
  });

  // Client-side: GET /templates accepts no query parameters and returns the full array,
  // so every row is already here and these counts are the real ones.
  const table = useClientTable<Template>(templatesQuery.data, (row) => [row.name, row.body]);

  /** Loops the existing single-delete endpoint — no batch API, this task has no backend scope. */
  async function runBulkDelete() {
    setBulkBusy(true);
    let ok = 0;
    let failed = 0;
    for (const id of [...selected]) {
      try {
        await api.delete(`/templates/${id}`);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setBulkBusy(false);
    setSelected(new Set());
    setBulkDeleteOpen(false);
    void queryClient.invalidateQueries({ queryKey: ["templates"] });
    const msg = failed
      ? `Deleted ${ok}, ${failed} failed.`
      : `Deleted ${ok} template${ok === 1 ? "" : "s"}.`;
    if (failed) toast.error(msg);
    else toast.success(msg);
  }

  const columns: TableColumn<Template>[] = [
    {
      key: "name",
      header: "Name",
      render: (row) => (
        <Highlight text={row.name} query={table.search} className="font-medium text-text" />
      ),
    },
    {
      key: "body",
      header: "Body preview",
      render: (row) => (
        <Highlight
          text={truncate(row.body, 80)}
          query={table.search}
          className="text-text-muted"
        />
      ),
    },
    {
      key: "createdAt",
      header: "Created",
      width: "140px",
      render: (row) => (
        <span className="text-text-muted">{formatDate(row.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "",
      width: "110px",
      align: "right",
      render: (row) => (
        <RowActions>
          <RowAction icon={<Pencil className="size-3.5" aria-hidden />} onClick={() => openEdit(row)}>
            Edit
          </RowAction>
          <RowAction
            tone="danger"
            icon={<Trash2 className="size-3.5" aria-hidden />}
            onClick={() => setDeleteTarget(row)}
          >
            Delete
          </RowAction>
        </RowActions>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        sticky
        title="Templates"
        description="Reusable message bodies you can reference when sending SMS."
        actions={
          <Button leftIcon={<Plus className="size-4" aria-hidden />} onClick={openCreate}>
            New template
          </Button>
        }
      />

      {templatesQuery.isError && (
        <p
          role="alert"
          className="rounded-control bg-danger-soft px-3 py-2 text-sm text-danger"
        >
          {apiErrorMessage(templatesQuery.error, "Could not load templates.")}
        </p>
      )}

      <Toolbar sticky>
        <ToolbarGrow className="sm:max-w-xs">
          <SearchInput
            value={table.search}
            onChange={table.setSearch}
            placeholder="Search name or body…"
          />
        </ToolbarGrow>
      </Toolbar>
      {selected.size > 0 && (
        <BulkActionBar
          count={selected.size}
          noun="template"
          onClear={() => setSelected(new Set())}
        >
          <Button
            size="sm"
            variant="secondary"
            disabled={bulkBusy}
            onClick={() => setBulkDeleteOpen(true)}
          >
            Delete
          </Button>
        </BulkActionBar>
      )}

      <Table
        stickyFirstColumn
        stickyHeader
        selectedIds={selected}
        onSelectionChange={setSelected}
        columns={columns}
        data={table.rows}
        getRowId={(row) => row._id}
        loading={templatesQueryLoading}
        emptyState={
          table.search ? (
            <EmptyState
              icon={<FileText className="size-6" aria-hidden />}
              title={`No results for "${table.search}"`}
              description="Try a different name, or clear the search to see every template."
            />
          ) : (
            <EmptyState
              icon={<FileText className="size-6" aria-hidden />}
              title="No templates yet"
              description="Create a template to reuse message bodies across your campaigns."
              action={<Button onClick={openCreate}>New template</Button>}
            />
          )
        }
      />

      <Modal
        open={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        title={`Delete ${selected.size} template${selected.size === 1 ? "" : "s"}?`}
        description="Messages already sent using them are not affected."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setBulkDeleteOpen(false)}>
              Keep them
            </Button>
            <Button variant="danger" loading={bulkBusy} onClick={() => void runBulkDelete()}>
              Delete {selected.size}
            </Button>
          </div>
        }
      />

      <Pagination
        mode="counted"
        page={table.page}
        pageSize={table.pageSize}
        total={table.total}
        onPageChange={table.setPage}
        onPageSizeChange={table.setPageSize}
      />

      <Modal
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? "Edit template" : "New template"}
        description={
          editing
            ? "Update the name or body of this template."
            : "Give your template a name and write the message body."
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>
              Cancel
            </Button>
            <Button
              loading={saveMutation.isPending}
              disabled={name.trim() === "" || body.trim() === ""}
              onClick={() => saveMutation.mutate()}
            >
              {editing ? "Save changes" : "Create template"}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <Input
            label="Name"
            placeholder="Order confirmation"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="template-body"
              className="text-sm font-medium text-text"
            >
              Body
            </label>
            <textarea
              id="template-body"
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Your order #{{order_id}} has been confirmed."
              maxLength={1600}
              className="min-h-[6rem] resize-y rounded-control border border-border bg-surface px-3 py-2 text-sm text-text outline-none transition-colors duration-fast ease-standard placeholder:text-text-muted focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
            <p className="text-xs text-text-muted">
              {body.length}/1600 characters
            </p>
          </div>

        </div>
      </Modal>

      <Modal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title={`Delete "${deleteTarget?.name ?? ""}"?`}
        description="This template will be permanently removed. This cannot be undone."
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={deleteMutation.isPending}
              onClick={() =>
                deleteTarget && deleteMutation.mutate(deleteTarget._id)
              }
            >
              Delete
            </Button>
          </div>
        }
      />
    </div>
  );
}
